import { type FC } from 'react';

interface CustomerProposalCompleteProps {
  logoUrl?: string;
  downloadUrl: string;
}

export const CustomerProposalComplete: FC<CustomerProposalCompleteProps> = ({
  logoUrl,
  downloadUrl,
}) => {
  const handleDownload = () => {
    // Check if we have a valid download URL
    if (!downloadUrl || downloadUrl === '#') {
      alert(
        'Download is not available at this time. Please contact support if you need a copy of your proposal.',
      );
      return;
    }

    // Create a temporary anchor element for downloading
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'proposal.pdf'; // Suggest a filename
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header with Logo */}
      <div className="bg-white py-6">
        <div className="text-center">
          {logoUrl && <img src={logoUrl} alt="Kalos Services" className="mx-auto h-12 w-auto" />}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 items-center justify-center px-6 py-8">
        <div className="w-full max-w-4xl">
          {/* Large Mint Box */}
          <div
            className="rounded-lg border border-gray-200 px-16 py-8 text-center"
            style={{ backgroundColor: 'rgb(235 251 238)' }}
          >
            {/* Green Checkmark Icon */}
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Thank You Message - directly on mint background */}
            <h1 className="mb-4 text-3xl font-bold text-gray-900">Thank You!</h1>
            <p className="mb-8 text-lg text-gray-700">
              Your proposal has been successfully accepted
            </p>

            {/* Small White Box inside Mint Box */}
            <div className="mx-8 mb-12 rounded-lg bg-white p-8 text-gray-700">
              <p className="mb-4">
                We have received your signed proposal and will be in touch shortly to schedule the
                next steps.
              </p>
              <p>A copy has also been emailed to you for your records.</p>
              {(!downloadUrl || downloadUrl === '#') && (
                <p className="mt-4 text-sm text-amber-600">
                  Note: The PDF download is being prepared and will be available shortly. Please
                  check your email for a copy.
                </p>
              )}
            </div>

            {/* Action Buttons - on mint background */}
            <div className="flex flex-col justify-center gap-4 px-4 pb-8 pt-4 sm:flex-row">
              <button
                onClick={handleDownload}
                disabled={!downloadUrl || downloadUrl === '#'}
                className={`inline-flex items-center justify-center rounded-md px-6 py-3 font-medium transition-colors ${
                  !downloadUrl || downloadUrl === '#'
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {!downloadUrl || downloadUrl === '#' ? 'Download Not Available' : 'Download PDF'}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                  />
                </svg>
                Print Proposal
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-white py-6">
        <div className="text-center text-gray-600">
          <p className="mb-2">If you have any questions, please contact us at:</p>
          <p>
            <a href="mailto:office@kalosflorida.com" className="text-blue-600 hover:text-blue-800">
              office@kalosflorida.com
            </a>
            {' | '}
            <a href="tel:(352)243-7099" className="text-blue-600 hover:text-blue-800">
              (352) 243-7099
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 py-3 text-center text-white">
        <p className="text-sm">©2025 Kalos Services, Inc. All rights reserved.</p>
      </div>
    </div>
  );
};
